import React, { createContext, useContext, useState, useEffect } from 'react';
import { safeFetch, safePost } from '../utils/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const [verificationCodes, setVerificationCodes] = useState(null);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me?token=${token}`);
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        logout();
      }
    } catch (err) {
      console.error('Failed to fetch user', err);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const data = await safePost(`${API_URL}/api/auth/login`, { email, password });
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem('auth_token', data.token);
    return data;
  };

  const register = async (name, email, password, phone) => {
    const data = await safePost(`${API_URL}/api/auth/register`, { name, email, password, phone });
    setToken(data.token);
    setUser(data.user);
    setVerificationCodes({ email_code: data.email_code, phone_code: data.phone_code });
    localStorage.setItem('auth_token', data.token);
    return data;
  };

  const verifyEmail = async (code) => {
    const res = await fetch(`${API_URL}/api/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: user?._id, code })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Verification failed');
    await fetchUser();
    return data;
  };

  const verifyPhone = async (code) => {
    const res = await fetch(`${API_URL}/api/auth/verify-phone`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: user?._id, code })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Verification failed');
    await fetchUser();
    return data;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setVerificationCodes(null);
    localStorage.removeItem('auth_token');
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, verificationCodes,
      login, register, logout, fetchUser,
      verifyEmail, verifyPhone,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
